/**
 * Tidal-diamond detail. Tapping a charted tidal diamond (the lettered stream
 * arrows) opens this: the set and rate running NOW at that diamond, whether it's
 * springs or neaps, the time relative to HW Victoria the table is referred to,
 * and the spring/neap figures for this hour so a student can see where today's
 * rate sits between them. It reads the same stream model the chartwork and the
 * passage simulator use, at the moment on the time scrubber, so the diamond, the
 * arrows and the plot all agree.
 */
import { useShellStore } from '@/store/shell';
import { useEnvStore } from '@/store/environment';
import { streamAtPosition, tidalStateAt, TIDAL_DIAMONDS } from '@/tides';
import { formatBearing } from '@/nav';
import { Sheet } from './Sheet';

/** Nearest charted diamond to a tapped point, for naming and the hourly table. */
function nearestDiamond(pos: [number, number]) {
  let best = TIDAL_DIAMONDS[0];
  let bestD = Infinity;
  for (const d of TIDAL_DIAMONDS) {
    const dx = d.position[0] - pos[0];
    const dy = d.position[1] - pos[1];
    const dd = dx * dx + dy * dy;
    if (dd < bestD) {
      bestD = dd;
      best = d;
    }
  }
  return best;
}

export function DiamondSheet() {
  const diamondTap = useShellStore((s) => s.diamondTap);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);

  if (!diamondTap || !TIDAL_DIAMONDS.length) {
    return <Sheet name="diamond" title="Tidal diamond" sub="No diamond data loaded." />;
  }

  const diamond = nearestDiamond(diamondTap);
  const st = tidalStateAt(streamTimeMs);
  const now = streamAtPosition(diamondTap, streamTimeMs);
  const h = st.hoursFromHW;
  const hwLabel =
    Math.abs(h) < 0.05
      ? 'HW Victoria'
      : `HW Victoria ${h > 0 ? '+' : '−'}${Math.abs(h).toFixed(1)} h`;
  const sn =
    st.springRatio >= 0.85 ? 'springs' : st.springRatio <= 0.15 ? 'neaps' : 'springs–neaps';

  // The diamond's own hourly row nearest this hour, for the spring/neap figures.
  const rowIndex = Math.max(0, Math.min(12, Math.round(h) + 6));
  const row = diamond.hours[rowIndex];

  return (
    <Sheet
      name="diamond"
      title={`Tidal diamond ${diamond.letter}`}
      sub={`Chart ${diamond.chart.toUpperCase()} · referred to ${diamond.refPort} · ${hwLabel} · ${sn}`}
    >
      <div className="acp-kv">
        <span className="k">Set now</span>
        <span className="v">{formatBearing(now.setDeg)}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Rate now</span>
        <span className="v">{now.rateKn.toFixed(1)} kn</span>
      </div>
      {row && (
        <>
          <div className="acp-kv">
            <span className="k">At this hour, springs</span>
            <span className="v">
              {formatBearing(row.dir)} · {row.sp.toFixed(1)} kn
            </span>
          </div>
          <div className="acp-kv">
            <span className="k">At this hour, neaps</span>
            <span className="v">
              {formatBearing(row.dir)} · {row.np.toFixed(1)} kn
            </span>
          </div>
        </>
      )}
      <p className="sub" style={{ marginTop: 8 }}>
        Set the moment with the time scrubber to step the stream through the tide. RYA training data
        referred to {diamond.refPort}; not for real-world navigation.
      </p>
    </Sheet>
  );
}
